import { createHash } from "node:crypto";
import { existsSync, mkdirSync, readFileSync, statSync, writeFileSync } from "node:fs";
import { dirname, join } from "node:path";
import { minimatch } from "minimatch";
import { parse, stringify } from "yaml";
import type { GitClient } from "./git/GitClient.js";
import type { LockfileManager } from "./LockfileManager.js";
import { ReplayDetector } from "./ReplayDetector.js";
import { isBinaryFile } from "./shared/binary.js";
import type { CustomizationsConfig, StoredPatch } from "./types.js";

export interface MigrationAnalysis {
    /** Files in .fernignore that have git commit history (can be tracked by Replay) */
    trackedByBoth: Array<{ file: string; commit: string }>;
    /** Files in .fernignore but NO recent commit history found after last generation */
    fernignoreOnly: string[];
    /** Commits found that AREN'T in .fernignore (inline edits to generated files) */
    commitsOnly: StoredPatch[];
    /** Synthetic patches created for .fernignore files that differ from pristine generation */
    syntheticPatches: StoredPatch[];
}

export interface MigrationResult {
    patchesCreated: number;
    filesSkipped: string[];
    warnings: string[];
}

export class FernignoreMigrator {
    private git: GitClient;
    private lockManager: LockfileManager;
    private outputDir: string;

    constructor(git: GitClient, lockManager: LockfileManager, outputDir: string) {
        this.git = git;
        this.lockManager = lockManager;
        this.outputDir = outputDir;
    }

    fernignoreExists(): boolean {
        return existsSync(join(this.outputDir, ".fernignore"));
    }

    readFernignorePatterns(): string[] {
        const fernignorePath = join(this.outputDir, ".fernignore");
        if (!existsSync(fernignorePath)) {
            return [];
        }
        const content = readFileSync(fernignorePath, "utf-8");
        return content
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line && !line.startsWith("#"));
    }

    /** Analyze .fernignore patterns vs git history. Creates synthetic patches for files differing from pristine generation. */
    async analyzeMigration(): Promise<MigrationAnalysis> {
        const patterns = this.readFernignorePatterns();
        const detector = new ReplayDetector(this.git, this.lockManager, this.outputDir);
        const { patches } = await detector.detectNewPatches();

        const trackedByBoth: Array<{ file: string; commit: string }> = [];
        const fernignoreOnly: string[] = [];
        const commitsOnly: StoredPatch[] = [];
        const syntheticPatches: StoredPatch[] = [];

        // Check each fernignore pattern against recent patches
        for (const pattern of patterns) {
            const matchingPatch = patches.find((p) => p.files.some((f) => minimatch(f, pattern) || f === pattern));
            if (matchingPatch) {
                trackedByBoth.push({
                    file: pattern,
                    commit: matchingPatch.original_commit
                });
            } else {
                fernignoreOnly.push(pattern);
            }
        }

        // For fernignore-only patterns, try to create synthetic patches
        // by diffing current file state against pristine generation tree
        const lock = this.lockManager.read();
        const currentGen = lock.generations.find((g) => g.commit_sha === lock.current_generation);

        if (currentGen && fernignoreOnly.length > 0) {
            const resolvedFiles = await this.resolvePatterns(fernignoreOnly);
            const remainingFernignoreOnly: string[] = [];

            for (const { pattern, files } of resolvedFiles) {
                const patchFiles: string[] = [];
                const diffParts: string[] = [];

                for (const filePath of files) {
                    const pristine = await this.git.showFile(currentGen.tree_hash, filePath);
                    const currentContent = this.readFileContent(filePath);

                    if (currentContent === null) {
                        // File in .fernignore but not on disk — nothing to track
                        continue;
                    }

                    if (pristine === null) {
                        // File doesn't exist in generation tree — it's a wholly new file
                        patchFiles.push(filePath);
                        diffParts.push(this.createNewFileDiff(filePath, currentContent));
                    } else if (pristine !== currentContent) {
                        // File differs from pristine — user has customized it
                        patchFiles.push(filePath);
                        diffParts.push(this.createFileDiff(filePath, pristine, currentContent));
                    }
                    // If pristine === currentContent, file is unchanged — skip
                }

                if (patchFiles.length > 0) {
                    const patchContent = diffParts.join("\n");
                    const contentHash = `sha256:${createHash("sha256").update(patchContent).digest("hex")}`;

                    // Snapshot THEIRS from the customer's working tree (the
                    // diff is pristine → currentContent on disk). Skip
                    // binary files (utf-8 read of binary content is lossy).
                    const theirsSnapshot: Record<string, string> = {};
                    for (const filePath of patchFiles) {
                        if (isBinaryFile(filePath)) continue;
                        const c = this.readFileContent(filePath);
                        if (c != null) theirsSnapshot[filePath] = c;
                    }

                    syntheticPatches.push({
                        id: `patch-fernignore-${createHash("sha256").update(pattern).digest("hex").slice(0, 8)}`,
                        content_hash: contentHash,
                        original_commit: currentGen.commit_sha,
                        original_message: `[fernignore-migration] Customizations for ${pattern}`,
                        original_author: "Fern Replay <replay@buildwithfern.com>",
                        base_generation: currentGen.commit_sha,
                        files: patchFiles,
                        patch_content: patchContent,
                        ...(Object.keys(theirsSnapshot).length > 0 ? { theirs_snapshot: theirsSnapshot } : {}),
                        // Synthetic patches captured from .fernignore-protected files
                        // are user-owned by definition — the customer's content
                        // diverges from the generator's pristine output, and the
                        // generator never produced this divergent content. Without
                        // this flag, removing the file from .fernignore later (so
                        // it leaves the protection check in `isFileUserOwned`) would
                        // expose the patch to absorption on the next regen,
                        // silently losing the customization.
                        user_owned: true
                    });

                    trackedByBoth.push({
                        file: pattern,
                        commit: `synthetic (differs from generated)`
                    });
                } else {
                    // No diff found — file matches generated output or doesn't exist
                    remainingFernignoreOnly.push(pattern);
                }
            }

            // Replace fernignoreOnly with only truly untrackable patterns
            fernignoreOnly.length = 0;
            fernignoreOnly.push(...remainingFernignoreOnly);
        }

        // Find patches that touch files NOT in .fernignore
        for (const patch of patches) {
            const hasUnprotectedFiles = patch.files.some((f) => !patterns.some((p) => minimatch(f, p) || f === p));
            if (hasUnprotectedFiles) {
                commitsOnly.push(patch);
            }
        }

        return { trackedByBoth, fernignoreOnly, commitsOnly, syntheticPatches };
    }

    private async resolvePatterns(patterns: string[]): Promise<Array<{ pattern: string; files: string[] }>> {
        const allFiles = (await this.git.exec(["ls-files"])).trim().split("\n").filter(Boolean);
        const results: Array<{ pattern: string; files: string[] }> = [];

        for (const pattern of patterns) {
            const matching = allFiles.filter(
                (f) => minimatch(f, pattern) || f === pattern || f.startsWith(pattern + "/")
            );
            results.push({ pattern, files: matching.length > 0 ? matching : [pattern] });
        }

        return results;
    }

    private readFileContent(filePath: string): string | null {
        const fullPath = join(this.outputDir, filePath);
        if (!existsSync(fullPath)) {
            return null;
        }
        try {
            const stat = statSync(fullPath);
            if (stat.isDirectory()) {
                return null;
            }
            return readFileSync(fullPath, "utf-8");
        } catch {
            return null;
        }
    }

    private createNewFileDiff(filePath: string, content: string): string {
        const lines = content.split("\n");
        const hunks = lines.map((l) => `+${l}`).join("\n");
        return [
            `diff --git a/${filePath} b/${filePath}`,
            "new file mode 100644",
            `--- /dev/null`,
            `+++ b/${filePath}`,
            `@@ -0,0 +1,${lines.length} @@`,
            hunks
        ].join("\n");
    }

    private createFileDiff(filePath: string, pristine: string, current: string): string {
        const oldLines = pristine.split("\n");
        const newLines = current.split("\n");
        // Simple full-file replacement diff — not optimal but correct
        const removals = oldLines.map((l) => `-${l}`).join("\n");
        const additions = newLines.map((l) => `+${l}`).join("\n");
        return [
            `diff --git a/${filePath} b/${filePath}`,
            `--- a/${filePath}`,
            `+++ b/${filePath}`,
            `@@ -1,${oldLines.length} +1,${newLines.length} @@`,
            removals,
            additions
        ].join("\n");
    }

    async migrate(): Promise<MigrationResult> {
        const analysis = await this.analyzeMigration();
        const detector = new ReplayDetector(this.git, this.lockManager, this.outputDir);
        const { patches } = await detector.detectNewPatches();

        const warnings: string[] = [];
        let patchesCreated = 0;

        // Add all detected patches to lockfile
        for (const patch of patches) {
            this.lockManager.addPatch(patch);
            patchesCreated++;
        }

        if (patchesCreated > 0) {
            this.lockManager.save();
        }

        // Warn about fernignore-only files
        for (const file of analysis.fernignoreOnly) {
            warnings.push(
                `${file}: in .fernignore but no commit history found. Commit this file or keep in .fernignore as fallback.`
            );
        }

        return {
            patchesCreated,
            filesSkipped: analysis.fernignoreOnly,
            warnings
        };
    }

    movePatternsToReplayYml(patterns: string[]): void {
        const replayYmlPath = join(this.outputDir, ".fern", "replay.yml");
        let config: CustomizationsConfig = {};

        if (existsSync(replayYmlPath)) {
            const content = readFileSync(replayYmlPath, "utf-8");
            config = (parse(content) as CustomizationsConfig) ?? {};
        }

        const existing = config.exclude ?? [];
        const merged = [...new Set([...existing, ...patterns])];
        config.exclude = merged;

        const dir = dirname(replayYmlPath);
        if (!existsSync(dir)) {
            mkdirSync(dir, { recursive: true });
        }
        writeFileSync(replayYmlPath, stringify(config, { lineWidth: 0 }), "utf-8");
    }
}
